"use client";

import { useToast } from "@/components/application/toast/ToastProvider";
import { StarIcon } from "@/components/foundations/rating-stars";
import { useHasFeatureAccess } from "@/hooks/useHasFeatureAccess";
import { cx } from "@/utils/cx";

import { useFavoriteMutation, useUnfavoriteMutation } from "../hooks/useFavoriteMutation";

interface FavoriteStarProps {
	poolAddress: string;
	network?: string;
	isFavorited: boolean;
	isLoading?: boolean;
	onUnfavoriting?: (poolAddress: string) => void;
}

export function FavoriteStar({
	poolAddress,
	network,
	isFavorited,
	isLoading: externalLoading,
	onUnfavoriting,
}: FavoriteStarProps) {
	const favoriteMutation = useFavoriteMutation();
	const unfavoriteMutation = useUnfavoriteMutation();
	const { showToast } = useToast();
	const hasFavoritesAccess = useHasFeatureAccess("favorites");

	const isLoading = externalLoading || favoriteMutation.isPending || unfavoriteMutation.isPending;

	const favoriteInput = { poolAddress, network };

	const handleClick = async () => {
		if (!hasFavoritesAccess) {
			showToast({
				message: "Favorites feature requires Pro or Max plan",
				type: "error",
				duration: 5000,
			});
			return;
		}

		if (isFavorited) {
			// Notify that unfavoriting is starting
			onUnfavoriting?.(poolAddress);
			// Unfavorite immediately
			try {
				await unfavoriteMutation.mutateAsync(favoriteInput);
				showToast({
					message: "Removed from favorites",
					type: "success",
					duration: 6000,
					undo: async () => {
						try {
							await favoriteMutation.mutateAsync(favoriteInput);
						} catch (error) {
							showToast({
								message: "Failed to restore favorite",
								type: "error",
								duration: 5000,
							});
						}
					},
				});
			} catch (error) {
				showToast({
					message: error instanceof Error ? error.message : "Failed to remove favorite",
					type: "error",
					duration: 5000,
				});
			}
		} else {
			// Favorite immediately
			try {
				await favoriteMutation.mutateAsync(favoriteInput);
				showToast({
					message: "Added to favorites",
					type: "success",
					duration: 3000,
				});
			} catch (error) {
				showToast({
					message: error instanceof Error ? error.message : "Failed to add favorite",
					type: "error",
					duration: 5000,
				});
			}
		}
	};

	// Don't render if user doesn't have access
	if (!hasFavoritesAccess) {
		return null;
	}

	return (
		<button
			type="button"
			onClick={handleClick}
			disabled={isLoading}
			className={cx(
				"flex size-10 shrink-0 pressable cursor-pointer items-center justify-center rounded-lg text-fg-quaternary transition-colors",
				"hover:bg-primary_hover hover:text-fg-quaternary_hover",
				"focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring",
				isLoading && "cursor-not-allowed opacity-50",
			)}
			aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}
			aria-pressed={isFavorited}
		>
			<StarIcon
				progress={isFavorited ? 100 : 0}
				className={cx(
					"size-5 transition-inherit-all",
					isFavorited ? "text-fg-brand-primary" : "text-current",
				)}
			/>
		</button>
	);
}
